import * as React from 'react'

import { useLiveRefresh, useLiveStatus } from '@/lib/realtime'
import { useSession } from '@/lib/session'
import {
  fetchNotifications,
  markNotificationsRead,
  type HubNotification,
} from './notifications-api'

/**
 * Estado do sino, uma vez para o app inteiro (o sino no topo e a página
 * `/notificacoes` leem o mesmo).
 *
 * Ao vivo: aviso novo chega na hora (tempo real). Sem conexão, consulta a
 * cada 30 s (só com a aba visível) e ao voltar o foco para a janela. Aviso para mim nasce de ação de OUTRA pessoa, então
 * não há o que atualizar depois das minhas próprias ações.
 */

const POLL_MS = 30_000

interface NotificationsState {
  readonly items: readonly HubNotification[]
  readonly unread: number
  readonly loading: boolean
  readonly error: string | null
  readonly refresh: () => Promise<void>
  readonly markRead: (ids: readonly string[]) => Promise<void>
  readonly markAllRead: () => Promise<void>
}

const NotificationsContext = React.createContext<NotificationsState | null>(null)

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { status } = useSession()
  const [items, setItems] = React.useState<readonly HubNotification[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const enabled = status === 'autenticado'

  const refresh = React.useCallback(async () => {
    const result = await fetchNotifications()
    setLoading(false)
    if (!result.ok) {
      setError(result.reason)
      return
    }
    setError(null)
    setItems(result.data)
  }, [])

  const live = useLiveStatus() === 'ao_vivo'
  useLiveRefresh(['notification'], refresh, { delayMs: 200, enabled })

  React.useEffect(() => {
    if (!enabled) return
    void refresh()
    const timer = window.setInterval(() => {
      if (!live && document.visibilityState === 'visible') void refresh()
    }, POLL_MS)
    const onFocus = () => void refresh()
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [enabled, refresh, live])

  const markRead = React.useCallback(async (ids: readonly string[]) => {
    const pending = ids.filter((id) => items.some((n) => n.id === id && !n.readAt))
    if (pending.length === 0) return
    // Otimista: o contador cai no clique; se o banco recusar, a próxima
    // consulta devolve o estado real.
    const now = new Date().toISOString()
    setItems((current) => current.map((n) => (pending.includes(n.id) ? { ...n, readAt: now } : n)))
    const result = await markNotificationsRead(pending)
    if (!result.ok) void refresh()
  }, [items, refresh])

  const markAllRead = React.useCallback(async () => {
    const now = new Date().toISOString()
    setItems((current) => current.map((n) => (n.readAt ? n : { ...n, readAt: now })))
    const result = await markNotificationsRead()
    if (!result.ok) void refresh()
  }, [refresh])

  const unread = React.useMemo(() => items.filter((n) => !n.readAt).length, [items])

  const value = React.useMemo<NotificationsState>(
    () => ({ items, unread, loading, error, refresh, markRead, markAllRead }),
    [items, unread, loading, error, refresh, markRead, markAllRead],
  )

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
}

export function useNotifications(): NotificationsState {
  const ctx = React.useContext(NotificationsContext)
  if (!ctx) throw new Error('useNotifications precisa estar dentro de <NotificationsProvider>')
  return ctx
}
